import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiPath from '../../utils/apiPath';

export const fetchStockMovements = createAsyncThunk(
  'stockMovement/fetchAll',
  async (
    { page = 1, limit = 10, type = '', status = '', date_range = '' } = {},
    { rejectWithValue }
  ) => {
    try {
      let url = `/api/v1.0/stock-movements?page=${page}&limit=${limit}`;
      if (type)       url += `&type=${encodeURIComponent(type)}`;
      if (status)     url += `&status=${encodeURIComponent(status)}`;
      if (date_range) url += `&date_range=${date_range}`;
      const response = await apiPath.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchStockMovementList = createAsyncThunk(
  'stockMovement/fetchList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiPath.get('/api/v1.0/stock-movements/list');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchStockMovementById = createAsyncThunk(
  'stockMovement/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/stock-movements/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createStockMovement = createAsyncThunk(
  'stockMovement/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiPath.post('/api/v1.0/stock-movements/create', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateStockMovement = createAsyncThunk(
  'stockMovement/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiPath.put(`/api/v1.0/stock-movements/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteStockMovement = createAsyncThunk(
  'stockMovement/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiPath.delete(`/api/v1.0/stock-movements/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const stockMovementSlice = createSlice({
  name: 'stockMovement',
  initialState: {
    movements:       [],
    movementList:    [],
    currentMovement: null,
    loading:         false,
    error:           null,
    totalRecord:     0,
    totalPage:       0,
    currentPage:     1,
    perPage:         10,
  },
  reducers: {
    clearStockMovementError:   (state) => { state.error = null; },
    clearCurrentMovement:      (state) => { state.currentMovement = null; },
    setCurrentPage:            (state, action) => { state.currentPage = action.payload; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStockMovements.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchStockMovements.fulfilled, (state, action) => {
        state.loading      = false;
        state.movements    = action.payload.movements || [];
        state.totalRecord  = action.payload.totalRecord  || 0;
        state.totalPage    = action.payload.totalPage    || 0;
        state.currentPage  = action.payload.currentPage  || 1;
        state.perPage      = action.payload.perPage      || 10;
      })
      .addCase(fetchStockMovements.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      .addCase(fetchStockMovementList.pending,   (state) => { state.loading = true; })
      .addCase(fetchStockMovementList.fulfilled, (state, action) => {
        state.loading      = false;
        state.movementList = action.payload?.data?.movements || action.payload?.movements || [];
      })
      .addCase(fetchStockMovementList.rejected,  (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      .addCase(fetchStockMovementById.pending,   (state) => { state.loading = true; })
      .addCase(fetchStockMovementById.fulfilled, (state, action) => {
        state.loading          = false;
        state.currentMovement  = action.payload;
      })
      .addCase(fetchStockMovementById.rejected,  (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      .addCase(createStockMovement.pending,   (state) => { state.loading = true; })
      .addCase(createStockMovement.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.id) {
          state.movements   = [action.payload, ...state.movements];
          state.totalRecord = state.totalRecord + 1;
        }
      })
      .addCase(createStockMovement.rejected,  (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      .addCase(updateStockMovement.pending,   (state) => { state.loading = true; })
      .addCase(updateStockMovement.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const index   = state.movements.findIndex((m) => m.id === updated.id);
        if (index !== -1) state.movements[index] = updated;
        if (state.currentMovement?.id === updated.id) state.currentMovement = updated;
      })
      .addCase(updateStockMovement.rejected,  (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      .addCase(deleteStockMovement.pending,   (state) => { state.loading = true; })
      .addCase(deleteStockMovement.fulfilled, (state, action) => {
        state.loading     = false;
        state.movements   = state.movements.filter((m) => m.id !== action.payload);
        state.totalRecord = Math.max(0, state.totalRecord - 1);
      })
      .addCase(deleteStockMovement.rejected,  (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      });
  },
});

export const { clearStockMovementError, clearCurrentMovement, setCurrentPage } =
  stockMovementSlice.actions;
export default stockMovementSlice.reducer;